import type { MapLocationDto } from '@/core/data/location/location.interface';
import { WebSpeedProvider, type SpeedProvider } from '@/core/domain/location/speed-provider';
import { gambits } from '@/core/gambits';
import { isPuzzleMoveCorrect } from '@/core/helpers/puzzle.helper';
import type { MapMarkerData } from '@/core/network/api/map-puzzle/map-puzzle.api';

import type {
  FieldStats,
  FinishSessionResponse,
  RevealResponse,
  RevealResultPuzzle,
  SessionSummaryResponse,
  StreakRecord,
  SyncAction,
  TraceTotals,
  WalkDailyCard,
  WalkMonthCard,
  WalkPuzzleData,
  WalkRedsResponse,
  WalkSessionState,
} from '@/core/network/api/walk/walk.api';
import { platform } from '@/core/platform';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useMapPuzzleStore } from '@/stores/mapPuzzle.store';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface WalkSettings {
  /** Selected walkfield band (a–h) */
  band: string;
  /** Selected walkfield speed (1–8 km/h) */
  speed: number;
  breakEvenSpeedMps: number;
  breakBankMs: number;
  /** Show puzzle result circles on the break board */
  showProgress: boolean;
  /** Show speed/break/puzzle info during active session */
  infoVisible: boolean;
  /** Enable map traces and spatial reveal view */
  mapEnabled: boolean;
}

export const TIME_BANK_MS = 1000;

export interface WalkPreset {
  id: string;
  label: string;
  band: string;
  speed: number;
  breakMin: number;
  isBuiltIn: boolean;
}

export const DEFAULT_PRESET: WalkPreset = {
  id: 'default',
  label: '5h',
  band: 'h',
  speed: 5,
  breakMin: 15,
  isBuiltIn: true,
};

const DEFAULT_SETTINGS: WalkSettings = {
  band: 'h',
  speed: 5,
  breakEvenSpeedMps: 5 / 3.6,
  breakBankMs: 900_000,
  showProgress: true,
  infoVisible: true,
  mapEnabled: false,
};

const SETTINGS_KEY = 'walk_settings';
const CUSTOM_PRESETS_KEY = 'walk_custom_presets';
const SPOTS_KEY = 'walk_spots';
const SESSION_KEY = 'walk_session';
const PUZZLES_KEY = 'walk_puzzles';
const ROUND_LOG_KEY = 'walk_round_log';
const SYNC_QUEUE_KEY = 'walk_sync_queue';
const TIMER_KEY = 'walk_timer_ms';
const LAST_REVEALED_KEY = 'walk_last_revealed_session';

function loadSettings(): WalkSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      delete parsed.timeBankMs;
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {
    /* use defaults */
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(s: WalkSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

function loadCustomPresets(): WalkPreset[] {
  try {
    const raw = localStorage.getItem(CUSTOM_PRESETS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* use defaults */
  }
  return [];
}

function saveCustomPresets(presets: WalkPreset[]) {
  localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(presets));
}

function loadSpots(): { start: MapLocationDto | null; reveal: MapLocationDto | null } {
  try {
    const raw = localStorage.getItem(SPOTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { start: parsed.start ?? null, reveal: parsed.reveal ?? null };
    }
  } catch {
    /* use defaults */
  }
  return { start: null, reveal: null };
}

function saveSpots(start: MapLocationDto | null, reveal: MapLocationDto | null) {
  localStorage.setItem(SPOTS_KEY, JSON.stringify({ start, reveal }));
}

function clearPersistedSpots() {
  localStorage.removeItem(SPOTS_KEY);
}

// --- Sync queue types ---

interface SyncEntry {
  id: string;
  type: 'startThink' | 'submitMove' | 'puzzleTimeout' | 'reveal';
  sessionId: string;
  payload: Record<string, unknown>;
  timestamp: number;
}

let syncEntrySeq = 0;
function nextSyncId(): string {
  return `${Date.now()}-${++syncEntrySeq}`;
}

interface RoundLogEntry {
  roundNumber: number;
  roundId?: string;
  moveUci: string | null;
  isCorrect: boolean | null;
  lat: number;
  lng: number;
  distanceM: number;
  timeRemainingMs: number;
  breakTimeRemainingMs?: number;
  timestamp: number;
  thinkSpeedMps?: number;
  thinkGpsSamples?: number;
}

// --- Session / puzzle persistence ---

let lastPersistedSessionJson: string | null = null;
let lastPersistedPuzzlesJson: string | null = null;
let lastPersistedSyncQueueJson: string | null = null;
let lastPersistedRoundLogJson: string | null = null;

function persistSession(s: WalkSessionState) {
  try {
    const json = JSON.stringify(s);
    if (json === lastPersistedSessionJson) return;
    lastPersistedSessionJson = json;
    localStorage.setItem(SESSION_KEY, json);
  } catch {
    /* quota exceeded */
  }
}

function loadPersistedSession(): WalkSessionState | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearPersistedSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(PUZZLES_KEY);
  localStorage.removeItem(ROUND_LOG_KEY);
  localStorage.removeItem(SYNC_QUEUE_KEY);
  localStorage.removeItem(TIMER_KEY);
  lastPersistedSessionJson = null;
  lastPersistedPuzzlesJson = null;
  lastPersistedSyncQueueJson = null;
  lastPersistedRoundLogJson = null;
}

function persistTimerMs(ms: number, round: number) {
  try {
    localStorage.setItem(TIMER_KEY, `${round}:${Math.round(ms)}`);
  } catch {
    /* quota exceeded */
  }
}

function loadPersistedTimerMs(round: number): number | null {
  try {
    const raw = localStorage.getItem(TIMER_KEY);
    if (!raw) return null;
    const sep = raw.indexOf(':');
    if (sep === -1) return null;
    const storedRound = Number(raw.slice(0, sep));
    if (storedRound !== round) return null;
    return Number(raw.slice(sep + 1));
  } catch {
    return null;
  }
}

function persistPuzzles(puzzles: WalkPuzzleData[]) {
  try {
    const json = JSON.stringify(puzzles);
    if (json === lastPersistedPuzzlesJson) return;
    lastPersistedPuzzlesJson = json;
    localStorage.setItem(PUZZLES_KEY, json);
  } catch {
    /* quota exceeded */
  }
}

function loadPersistedPuzzles(): WalkPuzzleData[] {
  try {
    const raw = localStorage.getItem(PUZZLES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistSyncQueue(queue: SyncEntry[]) {
  try {
    const json = JSON.stringify(queue);
    if (json === lastPersistedSyncQueueJson) return;
    lastPersistedSyncQueueJson = json;
    localStorage.setItem(SYNC_QUEUE_KEY, json);
  } catch {
    /* quota exceeded */
  }
}

function loadSyncQueue(): SyncEntry[] {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistRoundLog(log: RoundLogEntry[]) {
  try {
    const json = JSON.stringify(log);
    if (json === lastPersistedRoundLogJson) return;
    lastPersistedRoundLogJson = json;
    localStorage.setItem(ROUND_LOG_KEY, json);
  } catch {
    /* quota exceeded */
  }
}

function loadRoundLog(): RoundLogEntry[] {
  try {
    const raw = localStorage.getItem(ROUND_LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const GPS_FRESH_THRESHOLD_MS = 10_000;
const GPS_TIMEOUT_MS = 5_000;

export const useWalkStore = defineStore(GamStoreId.WALK, () => {
  const isLoading = ref(false);
  const dailyCards = ref<WalkDailyCard[]>([]);
  const monthCards = ref<WalkMonthCard[]>([]);
  const todayCard = ref<WalkDailyCard | null>(null);
  const streakRecords = ref<StreakRecord[]>([]);
  const fieldStats = ref<FieldStats[]>([]);
  const traceTotals = ref<TraceTotals>({ traceMine: 0, traceOthers: 0 });

  const fieldBrilliantMap = computed(() => {
    const map = new Map<string, number>();
    for (const fs of fieldStats.value) {
      map.set(fs.field, fs.topSessions[0]?.successPct ?? 0);
    }
    return map;
  });

  // --- Settings ---
  const settings = ref<WalkSettings>(loadSettings());
  const customPresets = ref<WalkPreset[]>(loadCustomPresets());

  const allPresets = computed<WalkPreset[]>(() => {
    const builtIn: WalkPreset = {
      id: 'default',
      label: `${settings.value.band.toUpperCase()}${settings.value.speed}`,
      band: settings.value.band,
      speed: settings.value.speed,
      breakMin: 15,
      isBuiltIn: true,
    };
    return [builtIn, ...customPresets.value];
  });

  function updateSettings(partial: Partial<WalkSettings>) {
    settings.value = { ...settings.value, ...partial };
    saveSettings(settings.value);
  }

  function addCustomPreset(preset: Omit<WalkPreset, 'id' | 'isBuiltIn'>) {
    const newPreset: WalkPreset = {
      ...preset,
      id: `custom-${Date.now()}`,
      isBuiltIn: false,
    };
    customPresets.value = [...customPresets.value, newPreset];
    saveCustomPresets(customPresets.value);
    return newPreset;
  }

  function removeCustomPreset(presetId: string) {
    customPresets.value = customPresets.value.filter((p) => p.id !== presetId);
    saveCustomPresets(customPresets.value);
  }

  // --- Session State ---
  const session = ref<WalkSessionState | null>(null);
  const currentPuzzle = ref<WalkPuzzleData | null>(null);
  const preloadedPuzzles = ref<WalkPuzzleData[]>([]);
  const sessionEndState = ref<'timed_out' | 'completed' | 'red_walk_complete' | null>(null);
  const boardEndState = ref<'timed_out' | 'completed' | 'red_walk_complete' | null>(null);
  const lastReveal = ref<RevealResponse | null>(null);
  const lastSummary = ref<SessionSummaryResponse | null>(null);
  const historySummary = ref<SessionSummaryResponse | null>(null);
  const showRevealOnReturn = ref(false);
  const showWalkMarkers = ref(false);
  const lastRevealedSessionId = ref<string | null>(localStorage.getItem(LAST_REVEALED_KEY));

  // --- Offline sync ---
  const syncQueue = ref<SyncEntry[]>(loadSyncQueue());
  const roundLog = ref<RoundLogEntry[]>(loadRoundLog());
  let syncInProgress = false;
  let activeSyncPromise: Promise<void> | null = null;

  // Tracks a move made locally but not yet confirmed by the server.
  // Prevents the timer from firing a timeout for a round the user already played.
  const pendingSubmitRound = ref<number | null>(null);

  // --- Puzzle Timer (speed-based, per-puzzle) ---
  const localTimeMs = ref(0);
  let timerInterval: ReturnType<typeof setInterval> | null = null;
  let timerLastTick = 0;

  // --- Break Timer (simple countdown, cumulative) ---
  const breakTimeMs = ref(0);

  // --- Speed Provider ---
  const speedProvider: SpeedProvider = new WebSpeedProvider();
  const currentSpeedMps = ref(0);
  // Assumed good until tracking says otherwise, so nothing is claimed while idle.
  const hasGpsSignal = ref(true);
  let speedPollTimer: ReturnType<typeof setInterval> | null = null;

  // --- Warmup ---
  const WARMUP_REQUIRED_TICKS = 5;
  const WARMUP_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
  const warmupReady = ref(false);
  let warmupConsecutiveTicks = 0;
  let warmupLastFixTimestamp = 0;
  const warmupTicks = ref(0);
  let warmupTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const warmupProgress = computed(() => {
    if (breakNeedsWarmup.value) {
      if (breakWarmupReady.value) return 1;
      return breakWarmupTicks.value / WARMUP_REQUIRED_TICKS;
    }
    if (!isWarmup.value) return 0;
    if (warmupReady.value) return 1;
    return warmupTicks.value / WARMUP_REQUIRED_TICKS;
  });

  // --- Break warmup (mid-session re-acquisition) ---
  const breakNeedsWarmup = ref(false);
  const breakWarmupReady = ref(false);
  const breakWarmupTicks = ref(0);
  let breakWarmupConsecutiveTicks = 0;
  let breakWarmupLastFixTimestamp = 0;

  function checkBreakWarmup() {
    if (!breakNeedsWarmup.value || breakWarmupReady.value || !session.value) return;
    const fixTs = speedProvider.lastTimestamp;
    if (!fixTs || fixTs === breakWarmupLastFixTimestamp) return;
    breakWarmupLastFixTimestamp = fixTs;

    if (currentSpeedMps.value >= session.value.breakEvenSpeedMps) {
      breakWarmupConsecutiveTicks++;
    } else {
      breakWarmupConsecutiveTicks = 0;
    }
    breakWarmupTicks.value = Math.min(breakWarmupConsecutiveTicks, WARMUP_REQUIRED_TICKS);
    if (breakWarmupConsecutiveTicks >= WARMUP_REQUIRED_TICKS) {
      breakWarmupReady.value = true;
    }
  }

  function activateBreakWarmup() {
    breakNeedsWarmup.value = true;
    breakWarmupReady.value = false;
    breakWarmupTicks.value = 0;
    breakWarmupConsecutiveTicks = 0;
    breakWarmupLastFixTimestamp = 0;
  }

  function resetBreakWarmup() {
    breakNeedsWarmup.value = false;
    breakWarmupReady.value = false;
    breakWarmupTicks.value = 0;
    breakWarmupConsecutiveTicks = 0;
    breakWarmupLastFixTimestamp = 0;
  }

  function checkWarmupReady() {
    if (isBreak.value && !breakWarmupReady.value && session.value) {
      if (session.value.status === 'walking_reds') return;
      if (currentSpeedMps.value === 0 && !breakNeedsWarmup.value) {
        activateBreakWarmup();
      }
      if (breakNeedsWarmup.value) {
        checkBreakWarmup();
      }
      return;
    }
    if (warmupReady.value || !isWarmup.value || !session.value) return;
    const fixTs = speedProvider.lastTimestamp;
    if (!fixTs || fixTs === warmupLastFixTimestamp) return;
    warmupLastFixTimestamp = fixTs;

    if (currentSpeedMps.value >= session.value.breakEvenSpeedMps) {
      warmupConsecutiveTicks++;
    } else {
      warmupConsecutiveTicks = 0;
    }
    warmupTicks.value = Math.min(warmupConsecutiveTicks, WARMUP_REQUIRED_TICKS);
    if (warmupConsecutiveTicks >= WARMUP_REQUIRED_TICKS) {
      warmupReady.value = true;
    }
  }

  function startWarmupTimeout() {
    clearWarmupTimeout();
    warmupTimeoutHandle = setTimeout(async () => {
      warmupTimeoutHandle = null;
      if (session.value?.currentPhase === 'warmup') {
        const sid = session.value.id;
        reset();
        await deleteSession(sid);
      }
    }, WARMUP_TIMEOUT_MS);
  }

  function clearWarmupTimeout() {
    if (warmupTimeoutHandle != null) {
      clearTimeout(warmupTimeoutHandle);
      warmupTimeoutHandle = null;
    }
  }

  // --- Spots (persisted to localStorage) ---
  const savedSpots = loadSpots();
  const startSpot = ref<MapLocationDto | null>(savedSpots.start);
  const revealSpot = ref<MapLocationDto | null>(savedSpots.reveal);
  const sessionStartSpot = ref<MapLocationDto | null>(null);
  const sessionRevealSpot = ref<MapLocationDto | null>(null);

  function setSpot(role: 'start' | 'reveal', location: MapLocationDto | null) {
    if (role === 'start') startSpot.value = location;
    else revealSpot.value = location;
    saveSpots(startSpot.value, revealSpot.value);

    const markerType = role === 'start' ? 'walk_start' : 'walk_reveal';
    const walkStore = useMapPuzzleStore();
    walkStore.markers = walkStore.markers.filter((m) => m.puzzleType !== markerType);
  }

  function saveSessionSpot(role: 'start' | 'reveal', location: MapLocationDto) {
    if (role === 'start') sessionStartSpot.value = location;
    else sessionRevealSpot.value = location;

    const sessionId = lastReveal.value?.session?.id ?? lastSummary.value?.sessionId ?? historySummary.value?.sessionId;
    if (!sessionId) return;

    const startLocationId = role === 'start' ? location.id : undefined;
    const revealLocationId = role === 'reveal' ? location.id : undefined;
    gambits.walkApi.updateSpots(sessionId, startLocationId, revealLocationId).catch(() => {});
  }

  // --- Red Walk State ---
  const redWalkPuzzles = ref<WalkPuzzleData[]>([]);
  const currentRedWalkIndex = ref(0);
  const redWalkProgress = ref(0);
  const isWalkingReds = computed(() => session.value?.status === 'walking_reds');
  const hasWrongRounds = ref(false);
  const wrongCount = ref(0);

  const hasActiveSession = computed(
    () =>
      session.value?.status === 'active' ||
      session.value?.status === 'completed' ||
      session.value?.status === 'walking_reds' ||
      session.value?.status === 'revealed',
  );
  const isThinking = computed(() => session.value?.currentPhase === 'thinking');
  const isBreak = computed(() => session.value?.currentPhase === 'break');
  const isWarmup = computed(() => session.value?.currentPhase === 'warmup');
  const isIdle = computed(() => !session.value || session.value.currentPhase === 'idle');
  const isRevealed = computed(() => session.value?.status === 'revealed');

  // --- Timer loop (setInterval-based, survives tab backgrounding) ---

  const TIMER_TICK_MS = 100;
  const TIMER_PERSIST_EVERY = 10; // persist every ~1s (10 ticks × 100ms)
  let timerTickCount = 0;

  function startTimerLoop() {
    if (session.value?.status === 'walking_reds') return;
    stopTimerLoop();
    timerLastTick = performance.now();
    timerTickCount = 0;
    timerInterval = setInterval(tickTimer, TIMER_TICK_MS);
  }

  function tickTimer() {
    const now = performance.now();
    const dtSec = Math.min((now - timerLastTick) / 1000, 2);
    timerLastTick = now;

    if (session.value?.currentPhase === 'thinking') {
      const breakEven = session.value.breakEvenSpeedMps;
      const rawRate = -1 + currentSpeedMps.value / breakEven;
      const netChange = (rawRate > 0 ? rawRate * 0.5 : rawRate) * dtSec * 1000;
      const newTime = localTimeMs.value + netChange;
      localTimeMs.value = Math.min(Math.max(0, Math.round(newTime)), session.value.timeBankMs);

      if (localTimeMs.value <= 0) {
        if (pendingSubmitRound.value === session.value.currentRound) {
          stopTimerLoop();
          return;
        }
        stopTimerLoop();
        handlePuzzleTimeout();
        return;
      }

      timerTickCount++;
      if (timerTickCount >= TIMER_PERSIST_EVERY) {
        timerTickCount = 0;
        persistTimerMs(localTimeMs.value, session.value.currentRound);
      }
    } else if (session.value?.currentPhase === 'break') {
      breakTimeMs.value = Math.max(0, Math.round(breakTimeMs.value - dtSec * 1000));

      if (breakTimeMs.value <= 0) {
        stopTimerLoop();
        boardEndState.value = 'timed_out';
        return;
      }
    }
  }

  function stopTimerLoop() {
    if (timerInterval != null) {
      clearInterval(timerInterval);
      timerInterval = null;
      localStorage.removeItem(TIMER_KEY);
    }
  }

  function recalcTimerFromSession() {
    if (!session.value || !session.value.phaseStartedAt) return;

    if (session.value.currentPhase === 'thinking') {
      const persisted = loadPersistedTimerMs(session.value.currentRound);
      if (persisted != null && persisted > 0) {
        localTimeMs.value = persisted;
      } else {
        const elapsed = Date.now() - new Date(session.value.phaseStartedAt).getTime();
        localTimeMs.value = Math.max(0, session.value.timeRemainingMs - elapsed);
      }
    } else if (session.value.currentPhase === 'break') {
      const elapsed = Date.now() - new Date(session.value.phaseStartedAt).getTime();
      breakTimeMs.value = Math.max(0, session.value.breakTimeRemainingMs - elapsed);
      localTimeMs.value = session.value.timeRemainingMs;
    }
  }

  // --- Speed tracking ---

  function startSpeedTracking() {
    stopSpeedTracking();
    speedProvider.start();
    speedPollTimer = setInterval(() => {
      currentSpeedMps.value = speedProvider.currentSpeedMps;
      hasGpsSignal.value = speedProvider.hasSignal;
      checkWarmupReady();
    }, 200);
  }

  function stopSpeedTracking() {
    speedProvider.stop();
    if (speedPollTimer != null) {
      clearInterval(speedPollTimer);
      speedPollTimer = null;
    }
    currentSpeedMps.value = 0;
    hasGpsSignal.value = true;
  }

  function getLastKnownLocation(): { lat: number; lng: number } | null {
    if (speedProvider.lastCoords) return speedProvider.lastCoords;
    const geo = useGeolocationStore();
    return geo.userLocation?.location ?? null;
  }

  async function getProximityLocation(): Promise<{ lat: number; lng: number } | null> {
    const cachedAge = speedProvider.lastTimestamp ? Date.now() - speedProvider.lastTimestamp : Infinity;
    return (cachedAge <= 3000 ? getLastKnownLocation() : null) ?? (await getFreshLocation()) ?? getLastKnownLocation();
  }

  async function getFreshLocation(): Promise<{ lat: number; lng: number } | null> {
    const geo = useGeolocationStore();

    if (geo.isLocationPermissionDenied) return null;

    const lastUpdate = geo.userLocation?.updatedAt;
    const age = lastUpdate ? Date.now() - new Date(lastUpdate).getTime() : Infinity;
    const loc = geo.userLocation?.location;

    if (geo.isLocationPermissionGranted) {
      if (loc && age < GPS_FRESH_THRESHOLD_MS) {
        return loc;
      }

      const coords = await Promise.race([
        geo.requestFreshPosition(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), GPS_TIMEOUT_MS)),
      ]);

      if (coords) {
        return { lat: coords.latitude, lng: coords.longitude };
      }

      // Permission is granted, so a miss here means the fix is slow or unavailable.
      return loc ?? null;
    }

    // Permission undecided: the system prompt may still be open, and its decision time
    // is excluded from the browser's own timeout — so no artificial deadline here.
    const coords = await geo.requestFreshPosition();
    return coords ? { lat: coords.latitude, lng: coords.longitude } : null;
  }

  // --- Session actions ---

  function applySessionState(state: WalkSessionState, persist = true) {
    session.value = state;

    if (state.currentPhase === 'warmup') {
      localTimeMs.value = state.timeRemainingMs;
      breakTimeMs.value = state.breakTimeRemainingMs;
      stopTimerLoop();
      startWarmupTimeout();
    } else if (pendingSubmitRound.value === state.currentRound && state.currentPhase === 'thinking') {
      localTimeMs.value = 0;
      stopTimerLoop();
    } else if (state.phaseStartedAt && state.currentPhase === 'thinking') {
      const persisted = loadPersistedTimerMs(state.currentRound);
      if (persisted != null && persisted > 0) {
        localTimeMs.value = persisted;
      } else {
        const elapsed = Date.now() - new Date(state.phaseStartedAt).getTime();
        localTimeMs.value = Math.max(0, state.timeRemainingMs - elapsed);
      }
      startTimerLoop();
    } else if (state.phaseStartedAt && state.currentPhase === 'break') {
      const elapsed = Date.now() - new Date(state.phaseStartedAt).getTime();
      breakTimeMs.value = Math.max(0, state.breakTimeRemainingMs - elapsed);
      localTimeMs.value = state.timeRemainingMs;
      startTimerLoop();
    } else {
      localTimeMs.value = state.timeRemainingMs;
      breakTimeMs.value = state.breakTimeRemainingMs;
      stopTimerLoop();
    }

    if (persist) persistSession(state);
  }

  async function createSession(startLocationId?: string, prefetchedLoc?: { lat: number; lng: number }) {
    isLoading.value = true;
    sessionEndState.value = null;
    boardEndState.value = null;
    lastReveal.value = null;
    lastSummary.value = null;
    lastRevealedSessionId.value = null;
    localStorage.removeItem(LAST_REVEALED_KEY);
    sessionStartSpot.value = null;
    sessionRevealSpot.value = null;
    warmupReady.value = false;
    warmupConsecutiveTicks = 0;
    warmupLastFixTimestamp = 0;
    warmupTicks.value = 0;
    try {
      const loc = prefetchedLoc ?? (await getFreshLocation());
      if (!loc) {
        return { success: false, reason: 'no_location' as const };
      }

      const mapTracesEnabled = platform.isOfflineCapable && !navigator.onLine ? false : settings.value.mapEnabled;

      const res = await gambits.walkApi.createSession(
        settings.value.band,
        TIME_BANK_MS,
        settings.value.breakEvenSpeedMps,
        loc.lat,
        loc.lng,
        startLocationId,
        settings.value.breakBankMs,
        mapTracesEnabled,
      );
      const data = res.data?.data;
      if (!data) return { success: false, reason: 'api_error' as const };

      applySessionState(data);
      preloadedPuzzles.value = data.allPuzzles ?? [];
      persistPuzzles(preloadedPuzzles.value);
      currentPuzzle.value = data.nextPuzzle;
      roundLog.value = [];
      syncQueue.value = [];
      persistRoundLog([]);
      persistSyncQueue([]);
      startSpeedTracking();

      if (data.startMarker) {
        const walkStore = useMapPuzzleStore();
        walkStore.markers = [...walkStore.markers.filter((m) => !m.puzzleType.startsWith('walk_')), data.startMarker];
      }

      return { success: true };
    } catch (err) {
      // The HTTP client rejects with an HttpError ({ status, error: ApiError }),
      // not a raw Axios error — read that shape so status/message actually surface.
      const httpErr = err as { status?: number; error?: { message?: string; errorKey?: string } };
      return {
        success: false,
        reason: 'api_error' as const,
        statusCode: httpErr?.status,
        errorMessage: httpErr?.error?.message,
        errorKey: httpErr?.error?.errorKey,
      };
    } finally {
      isLoading.value = false;
    }
  }

  async function loadActiveSession() {
    const persisted = loadPersistedSession();
    const persistedPuzzles = loadPersistedPuzzles();

    try {
      // Deliver any pending actions so the server has up-to-date state
      if (syncQueue.value.length > 0) {
        await flushSyncQueue();
      }

      const res = await gambits.walkApi.getActiveSession();
      const data = res.data?.data;
      if (!data || !('id' in data)) {
        session.value = null;
        sessionEndState.value = null;
        boardEndState.value = null;
        clearPersistedSession();
        if (persisted) {
          startSpot.value = null;
          revealSpot.value = null;
          clearPersistedSpots();
        }
        if (data && 'markers' in data) {
          const mapState = data as { markers: MapMarkerData[]; markerExpiresAt: string | null };
          const puzzleStore = useMapPuzzleStore();
          puzzleStore.markers = mapState.markers ?? [];
          puzzleStore.mapMarkerExpiresAt = mapState.markerExpiresAt ?? null;
        }
        return false;
      }

      // Local-first: if local state is ahead of server (sync queue pending),
      // keep local state for phase/timer and let the queue catch up.
      const localIsAhead =
        persisted &&
        syncQueue.value.length > 0 &&
        (persisted.currentRound > data.currentRound ||
          (persisted.currentRound === data.currentRound &&
            persisted.currentPhase === 'thinking' &&
            data.currentPhase !== 'thinking'));

      if (localIsAhead) {
        applySessionState(persisted, false);
        preloadedPuzzles.value = data.allPuzzles ?? persistedPuzzles;
        persistPuzzles(preloadedPuzzles.value);
        if (persisted.currentPhase === 'thinking') {
          currentPuzzle.value = getNextPreloadedPuzzle(persisted.currentRound);
        }
        startSpeedTracking();
        getFreshLocation();
        fireAndForgetSync();
        return true;
      }

      applySessionState(data);
      preloadedPuzzles.value = data.allPuzzles ?? [];
      persistPuzzles(preloadedPuzzles.value);
      currentPuzzle.value = data.nextPuzzle;

      // Server confirmed the round advanced or is in break — move was delivered
      if (pendingSubmitRound.value != null && data.currentPhase !== 'thinking') {
        pendingSubmitRound.value = null;
      }

      if (data.redWalkPuzzles && data.redWalkPuzzles.length > 0) {
        redWalkPuzzles.value = data.redWalkPuzzles;
        currentRedWalkIndex.value = 0;
      }

      if (data.status === 'revealed') {
        const wrongs = (data.rounds ?? []).filter((r) => r.isCorrect === false);
        hasWrongRounds.value = wrongs.length > 0;
        wrongCount.value = wrongs.length;
      }

      if (data.status === 'active' || data.status === 'completed' || data.status === 'walking_reds') {
        startSpeedTracking();
        getFreshLocation();
      }
      return true;
    } catch {
      if (persisted && persisted.status === 'active') {
        applySessionState(persisted, false);
        preloadedPuzzles.value = persistedPuzzles;
        roundLog.value = loadRoundLog();
        syncQueue.value = loadSyncQueue();
        if (persisted.currentPhase === 'thinking') {
          const rn = persisted.currentRound;
          currentPuzzle.value = getNextPreloadedPuzzle(rn);
        }
        startSpeedTracking();
        getFreshLocation();
        return true;
      }
      session.value = null;
      sessionEndState.value = null;
      boardEndState.value = null;
      clearPersistedSession();
      startSpot.value = null;
      revealSpot.value = null;
      clearPersistedSpots();
      return false;
    }
  }

  function getNextPreloadedPuzzle(roundNumber: number): WalkPuzzleData | null {
    return preloadedPuzzles.value.find((p) => p.roundNumber === roundNumber) ?? null;
  }

  async function startThink() {
    if (!session.value) return null;

    resetBreakWarmup();

    const isRedWalk = session.value.status === 'walking_reds';
    clearWarmupTimeout();
    if (!isRedWalk) stopTimerLoop();
    if (!isRedWalk) speedProvider.startThinkPhase();

    const distM = isRedWalk ? 0 : speedProvider.consumeSessionDistance();
    const timeMs = localTimeMs.value;
    const breakMs = breakTimeMs.value;

    const isFromBreak = session.value.currentPhase === 'break';
    const isFromIdle = session.value.currentPhase === 'idle' || session.value.currentPhase === 'warmup';

    let nextRound: number;
    if (isRedWalk) {
      const nextRedPuzzle = redWalkPuzzles.value[currentRedWalkIndex.value];
      nextRound = nextRedPuzzle?.roundNumber ?? 0;
    } else {
      nextRound = isFromIdle ? 1 : session.value.currentRound + (isFromBreak ? 1 : 0);
    }

    if (isFromBreak && !isRedWalk) {
      const newBreak = breakMs + session.value.timeBankMs;
      if (newBreak <= 0) {
        session.value = {
          ...session.value,
          status: 'timed_out',
          currentPhase: 'idle',
          phaseStartedAt: null,
          breakTimeRemainingMs: 0,
          endedAt: new Date().toISOString(),
        } as WalkSessionState & { endedAt: string };
        persistSession(session.value);
        currentPuzzle.value = null;
        sessionEndState.value = 'timed_out';
        boardEndState.value = 'timed_out';
        return { timedOut: true, session: session.value };
      }
      breakTimeMs.value = newBreak;
    }

    const preloaded = isRedWalk
      ? (redWalkPuzzles.value[currentRedWalkIndex.value] ?? null)
      : getNextPreloadedPuzzle(nextRound);

    if (preloaded) {
      currentPuzzle.value = preloaded;
      const now = new Date().toISOString();
      session.value = {
        ...session.value,
        currentPhase: 'thinking',
        currentRound: preloaded.roundNumber,
        timeRemainingMs: isRedWalk ? 0 : session.value.timeBankMs,
        breakTimeRemainingMs: isFromBreak ? breakTimeMs.value : session.value.breakTimeRemainingMs,
        phaseStartedAt: now,
        totalDistanceM: session.value.totalDistanceM + distM,
      };
      localTimeMs.value = isRedWalk ? 0 : session.value.timeBankMs;
      persistSession(session.value);
      startTimerLoop();
    }

    const loc = getLastKnownLocation() ?? (await getFreshLocation());

    enqueueSyncAction('startThink', {
      lat: loc?.lat ?? 0,
      lng: loc?.lng ?? 0,
      timeRemainingMs: Math.round(timeMs),
      breakTimeRemainingMs: Math.round(breakMs),
      distanceM: distM,
    });

    fireAndForgetSync();

    return preloaded ? { timedOut: false, puzzle: preloaded, session: session.value } : null;
  }

  function requestStartFromBreak(): boolean {
    if (!session.value || session.value.currentPhase !== 'break') return false;
    if (session.value.status === 'walking_reds') return true;

    if (breakNeedsWarmup.value && !breakWarmupReady.value) {
      return false;
    }
    return true;
  }

  async function submitMove(moveUci: string): Promise<{ success: boolean; submitFailed?: boolean; data?: any }> {
    if (!session.value || !currentPuzzle.value) return { success: false };

    const isRedWalk = session.value.status === 'walking_reds';
    if (!isRedWalk) stopTimerLoop();
    const thinkResult = isRedWalk ? { avgSpeedMps: 0, sampleCount: 0 } : speedProvider.endThinkPhase();

    const distM = isRedWalk ? 0 : speedProvider.consumeSessionDistance();
    const timeMs = localTimeMs.value;

    const isCorrect = isPuzzleMoveCorrect(
      currentPuzzle.value.fen,
      currentPuzzle.value.setupMove,
      currentPuzzle.value.solutionMove,
      moveUci,
    );
    let isLastRound: boolean;
    if (isRedWalk) {
      const nextIdx = currentRedWalkIndex.value + 1;
      isLastRound = nextIdx >= redWalkPuzzles.value.length;
    } else {
      isLastRound = session.value.currentRound >= 50;
    }

    const loc = getLastKnownLocation() ?? (await getFreshLocation());

    const alreadyLogged = roundLog.value.some(
      (l) => l.roundNumber === session.value!.currentRound && l.moveUci != null,
    );

    if (!alreadyLogged) {
      const logEntry: RoundLogEntry = {
        roundNumber: session.value.currentRound,
        roundId: currentPuzzle.value.roundId,
        moveUci,
        isCorrect,
        lat: loc?.lat ?? 0,
        lng: loc?.lng ?? 0,
        distanceM: distM,
        timeRemainingMs: timeMs,
        timestamp: Date.now(),
        thinkSpeedMps: thinkResult.sampleCount >= 3 ? thinkResult.avgSpeedMps : undefined,
        thinkGpsSamples: thinkResult.sampleCount,
      };
      roundLog.value = [...roundLog.value, logEntry];
      persistRoundLog(roundLog.value);
    }

    // Enqueue submitMove — sync queue serialises startThink + submitMove together
    const alreadyQueued = syncQueue.value.some((e) => e.type === 'submitMove' && e.payload.moveUci === moveUci);
    if (!alreadyQueued) {
      enqueueSyncAction('submitMove', {
        lat: loc?.lat ?? 0,
        lng: loc?.lng ?? 0,
        timeRemainingMs: Math.round(timeMs),
        distanceM: distM,
        moveUci,
      });
    }

    // Advance state optimistically
    const now = new Date().toISOString();

    if (isRedWalk) {
      if (!platform.isOfflineCapable) redWalkProgress.value += 1;
      if (isCorrect) {
        redWalkPuzzles.value = redWalkPuzzles.value.filter((_, i) => i !== currentRedWalkIndex.value);
      } else {
        currentRedWalkIndex.value += 1;
      }

      session.value = {
        ...session.value,
        currentPhase: isLastRound ? 'idle' : 'break',
        phaseStartedAt: isLastRound ? null : now,
      };
    } else {
      session.value = {
        ...session.value,
        currentPhase: isLastRound ? 'idle' : 'break',
        phaseStartedAt: isLastRound ? null : now,
        timeRemainingMs: session.value.timeBankMs,
        totalRounds: session.value.currentRound,
        totalDistanceM: session.value.totalDistanceM + distM,
        status: isLastRound ? 'completed' : 'active',
      };
    }

    persistSession(session.value);
    currentPuzzle.value = null;

    if (isLastRound && isRedWalk) {
      boardEndState.value = 'red_walk_complete';
    } else if (isLastRound) {
      boardEndState.value = 'completed';
    } else {
      breakTimeMs.value = session.value.breakTimeRemainingMs;
      startTimerLoop();
    }

    if (!platform.isOfflineCapable) {
      // Browser/PWA: await flush so we confirm delivery
      if (activeSyncPromise) await activeSyncPromise;
      await flushSyncQueue();

      if (syncQueue.value.some((e) => e.type === 'submitMove')) {
        stopTimerLoop();
        pendingSubmitRound.value = session.value.currentRound;
        return { success: false, submitFailed: true };
      }

      pendingSubmitRound.value = null;

      if (isLastRound && isRedWalk) {
        sessionEndState.value = 'red_walk_complete';
      }
    } else {
      // Native: fire-and-forget, sync catches up later
      fireAndForgetSync();
      if (isLastRound && isRedWalk) {
        sessionEndState.value = 'red_walk_complete';
      }
    }

    return { success: true, data: { isCorrect, isLastRound, isRedWalk, session: session.value } };
  }

  async function handlePuzzleTimeout() {
    if (!session.value) return;

    const isRedWalk = session.value.status === 'walking_reds';
    if (!isRedWalk) speedProvider.endThinkPhase();
    const distM = isRedWalk ? 0 : speedProvider.consumeSessionDistance();

    let isLastRound: boolean;
    if (isRedWalk) {
      const nextIdx = currentRedWalkIndex.value + 1;
      isLastRound = nextIdx >= redWalkPuzzles.value.length;
    } else {
      isLastRound = session.value.currentRound >= 50;
    }

    const loc = getLastKnownLocation();

    const logEntry: RoundLogEntry = {
      roundNumber: session.value.currentRound,
      roundId: currentPuzzle.value?.roundId,
      moveUci: null,
      isCorrect: null,
      lat: loc?.lat ?? 0,
      lng: loc?.lng ?? 0,
      distanceM: distM,
      timeRemainingMs: 0,
      timestamp: Date.now(),
    };
    roundLog.value = [...roundLog.value, logEntry];
    persistRoundLog(roundLog.value);

    const now = new Date().toISOString();

    if (isRedWalk) {
      if (!platform.isOfflineCapable) redWalkProgress.value += 1;
      currentRedWalkIndex.value += 1;
      session.value = {
        ...session.value,
        currentPhase: isLastRound ? 'idle' : 'break',
        phaseStartedAt: isLastRound ? null : now,
        timeRemainingMs: session.value.timeBankMs,
        totalDistanceM: session.value.totalDistanceM + distM,
      };
    } else {
      session.value = {
        ...session.value,
        currentPhase: isLastRound ? 'idle' : 'break',
        phaseStartedAt: isLastRound ? null : now,
        timeRemainingMs: session.value.timeBankMs,
        totalRounds: session.value.currentRound,
        totalDistanceM: session.value.totalDistanceM + distM,
        status: isLastRound ? 'completed' : 'active',
      };
    }
    persistSession(session.value);
    currentPuzzle.value = null;

    if (isLastRound) {
      stopTimerLoop();
      boardEndState.value = isRedWalk ? 'red_walk_complete' : 'completed';
    } else {
      breakTimeMs.value = session.value.breakTimeRemainingMs;
      startTimerLoop();
    }

    enqueueSyncAction('puzzleTimeout', {
      lat: loc?.lat ?? 0,
      lng: loc?.lng ?? 0,
      distanceM: distM,
    });

    if (!platform.isOfflineCapable && isLastRound) {
      if (activeSyncPromise) await activeSyncPromise;
      await flushSyncQueue();
      if (isRedWalk) {
        sessionEndState.value = 'red_walk_complete';
      }
    } else {
      fireAndForgetSync();
      if (isLastRound && isRedWalk) {
        sessionEndState.value = 'red_walk_complete';
      }
    }
  }

  // --- Sync queue helpers ---

  function enqueueSyncAction(type: SyncEntry['type'], payload: Record<string, unknown>): string {
    const id = nextSyncId();
    if (!session.value) return id;
    syncQueue.value = [...syncQueue.value, { id, type, sessionId: session.value.id, payload, timestamp: Date.now() }];
    persistSyncQueue(syncQueue.value);
    return id;
  }

  function dequeueSyncById(entryId: string) {
    syncQueue.value = syncQueue.value.filter((e) => e.id !== entryId);
    persistSyncQueue(syncQueue.value);
  }

  async function flushSyncQueue() {
    if (syncInProgress || syncQueue.value.length === 0 || !session.value) return;
    syncInProgress = true;

    const queue = [...syncQueue.value];
    const gameActions = queue.filter((e) => e.type !== 'reveal');
    const revealActions = queue.filter((e) => e.type === 'reveal');

    if (gameActions.length > 0) {
      const sessionId = gameActions[0].sessionId;

      const actions: SyncAction[] = gameActions.map((entry) => ({
        id: entry.id,
        type: entry.type as 'startThink' | 'submitMove' | 'puzzleTimeout',
        lat: entry.payload.lat as number,
        lng: entry.payload.lng as number,
        distanceM: (entry.payload.distanceM as number) ?? 0,
        timeRemainingMs: entry.payload.timeRemainingMs as number | undefined,
        breakTimeRemainingMs: entry.payload.breakTimeRemainingMs as number | undefined,
        moveUci: entry.payload.moveUci as string | undefined,
        timestamp: entry.timestamp,
      }));

      try {
        const res = await gambits.walkApi.syncSession(sessionId, actions);
        const data = res.data?.data;
        if (data) {
          const processed = new Set(data.processedIds);
          syncQueue.value = syncQueue.value.filter((e) => !processed.has(e.id));
          persistSyncQueue(syncQueue.value);
        }
      } catch {
        for (const entry of gameActions) {
          try {
            const p = entry.payload;
            if (entry.type === 'startThink') {
              await gambits.walkApi.startThink(
                entry.sessionId,
                p.lat as number,
                p.lng as number,
                p.timeRemainingMs as number,
                p.breakTimeRemainingMs as number,
                p.distanceM as number,
              );
            } else if (entry.type === 'submitMove') {
              await gambits.walkApi.submitMove(
                entry.sessionId,
                p.moveUci as string,
                p.lat as number,
                p.lng as number,
                p.timeRemainingMs as number,
                p.distanceM as number,
              );
            } else if (entry.type === 'puzzleTimeout') {
              await gambits.walkApi.puzzleTimeout(
                entry.sessionId,
                p.lat as number,
                p.lng as number,
                p.distanceM as number,
              );
            }
            dequeueSyncById(entry.id);
          } catch {
            syncInProgress = false;
            return;
          }
        }
      }
    }

    for (const entry of revealActions) {
      try {
        const p = entry.payload;
        const res = await gambits.walkApi.reveal(
          entry.sessionId,
          p.lat as number,
          p.lng as number,
          p.revealLocationId as string | undefined,
          p.distanceM as number | undefined,
        );
        const data = res.data?.data;
        if (data) {
          finalizeReveal(data);
        }
        dequeueSyncById(entry.id);
      } catch {
        break;
      }
    }

    syncInProgress = false;
  }

  function fireAndForgetSync() {
    if (syncQueue.value.length === 0) return;
    activeSyncPromise = flushSyncQueue().finally(() => {
      activeSyncPromise = null;
    });
  }

  function computeLocalReveal(loc: { lat: number; lng: number }): RevealResponse {
    const puzzleMap = new Map(preloadedPuzzles.value.map((p) => [p.roundNumber, p]));
    const submittedLogs = roundLog.value.filter((l) => l.moveUci != null);
    const solved = submittedLogs.filter((l) => l.isCorrect === true).length;
    const submitted = submittedLogs.length;

    const puzzles: RevealResultPuzzle[] = roundLog.value.map((log) => {
      const preloaded = puzzleMap.get(log.roundNumber);
      return {
        roundNumber: log.roundNumber,
        puzzleId: 0,
        fen: preloaded?.fen ?? '',
        setupMove: preloaded?.setupMove ?? '',
        solutionMove: preloaded?.solutionMove ?? '',
        rating: preloaded?.rating ?? 0,
        isCorrect: log.isCorrect,
        submitLat: log.lat,
        submitLng: log.lng,
        submittedMoveUci: log.moveUci,
        traceId: null,
      };
    });

    const wrongRounds = roundLog.value.filter((l) => l.isCorrect === false);

    const revealedSession: WalkSessionState = {
      ...session.value!,
      status: 'revealed',
      currentPhase: 'idle',
      phaseStartedAt: null,
      revealLat: loc.lat,
      revealLng: loc.lng,
    };

    return {
      session: revealedSession,
      revealResults: { solved, submitted, puzzles },
      hasWrongRounds: wrongRounds.length > 0,
      wrongCount: wrongRounds.length,
      startSpotName: null,
      revealSpotName: null,
      totalDurationMs: roundLog.value.length > 0 ? Date.now() - roundLog.value[0].timestamp : undefined,
      markers: [],
    };
  }

  async function reveal(revealLocationId?: string) {
    if (!session.value) return null;

    clearWarmupTimeout();
    stopTimerLoop();

    // Get location and remaining distance BEFORE stopping speed tracking
    // (clearWatch kills GPS hardware)
    const cachedAge = speedProvider.lastTimestamp ? Date.now() - speedProvider.lastTimestamp : Infinity;
    const loc =
      (cachedAge <= 3000 ? getLastKnownLocation() : null) ??
      (await getFreshLocation()) ??
      getLastKnownLocation() ??
      (session.value.originLat != null ? { lat: session.value.originLat, lng: session.value.originLng } : null);

    const revealDistanceM = speedProvider.consumeSessionDistance();
    stopSpeedTracking();
    if (!loc) return null;

    if (platform.isOfflineCapable && !navigator.onLine) {
      const localResult = computeLocalReveal(loc);

      enqueueSyncAction('reveal', {
        lat: loc.lat,
        lng: loc.lng,
        revealLocationId,
        distanceM: revealDistanceM,
      });

      finalizeReveal(localResult);
      // Re-persist sync queue after finalizeReveal (which clears localStorage)
      persistSyncQueue(syncQueue.value);
      return localResult;
    }

    // Online path (browser/PWA or native with connectivity)
    // Wait for any in-flight sync (e.g. submitMove flush) before proceeding,
    // otherwise the reveal API call can race ahead of undelivered round data.
    if (activeSyncPromise) await activeSyncPromise;

    const stillActive = await loadActiveSession();
    stopTimerLoop();
    stopSpeedTracking();
    if (!stillActive || !session.value) return null;

    await flushSyncQueue();

    try {
      const res = await gambits.walkApi.reveal(session.value.id, loc.lat, loc.lng, revealLocationId, revealDistanceM);
      const data = res.data?.data;
      if (!data) return null;

      finalizeReveal(data);
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        cleanupAfterReveal();
      }

      // Native fallback: if online reveal fails, use local reveal
      if (platform.isOfflineCapable) {
        const localResult = computeLocalReveal(loc);

        enqueueSyncAction('reveal', {
          lat: loc.lat,
          lng: loc.lng,
          revealLocationId,
          distanceM: revealDistanceM,
        });

        finalizeReveal(localResult);
        persistSyncQueue(syncQueue.value);
        return localResult;
      }

      // eslint-disable-next-line no-console
      console.error('[walk] reveal failed:', err);
      return null;
    }
  }

  function finalizeReveal(data: RevealResponse) {
    resetBreakWarmup();
    applySessionState(data.session);
    lastReveal.value = data;
    lastRevealedSessionId.value = data.session.id;
    localStorage.setItem(LAST_REVEALED_KEY, data.session.id);
    hasWrongRounds.value = data.hasWrongRounds ?? false;
    wrongCount.value = data.wrongCount ?? 0;

    const puzzleStore = useMapPuzzleStore();
    puzzleStore.markers = data.markers ?? [];

    loadDailyCards();
    loadTodayCard();

    stopSpeedTracking();
    stopTimerLoop();
    clearPersistedSession();
    startSpot.value = null;
    revealSpot.value = null;
    sessionStartSpot.value = null;
    sessionRevealSpot.value = null;
    clearPersistedSpots();
    sessionEndState.value = null;
    boardEndState.value = null;
    currentPuzzle.value = null;
    showWalkMarkers.value = false;
  }

  function cleanupAfterReveal() {
    stopSpeedTracking();
    stopTimerLoop();
    clearPersistedSession();
    startSpot.value = null;
    revealSpot.value = null;
    sessionStartSpot.value = null;
    sessionRevealSpot.value = null;
    clearPersistedSpots();
    sessionEndState.value = null;
    boardEndState.value = null;
    currentPuzzle.value = null;
    showWalkMarkers.value = false;
  }

  async function walkReds() {
    if (!session.value) return null;

    try {
      const res = await gambits.walkApi.walkReds(session.value.id);
      const data = res.data?.data as WalkRedsResponse;
      if (!data) return null;

      resetBreakWarmup();
      applySessionState(data.session);
      redWalkPuzzles.value = data.redWalkPuzzles;
      currentRedWalkIndex.value = 0;
      redWalkProgress.value = 0;
      sessionEndState.value = null;
      boardEndState.value = null;
      roundLog.value = [];
      persistRoundLog([]);

      if (settings.value.mapEnabled) {
        const walkStore = useMapPuzzleStore();
        walkStore.markers = walkStore.markers.filter((m) => m.puzzleType === 'walk_start');
      }

      const firstPuzzle = redWalkPuzzles.value[0];
      if (firstPuzzle) {
        currentPuzzle.value = firstPuzzle;
        session.value = {
          ...session.value!,
          currentPhase: 'thinking',
          currentRound: firstPuzzle.roundNumber,
          phaseStartedAt: new Date().toISOString(),
        };
        persistSession(session.value!);

        enqueueSyncAction('startThink', {
          lat: 0,
          lng: 0,
          timeRemainingMs: 0,
          breakTimeRemainingMs: 0,
          distanceM: 0,
        });
        fireAndForgetSync();
      }

      return data;
    } catch {
      return null;
    }
  }

  async function finishSession() {
    if (!session.value) return null;

    try {
      const res = await gambits.walkApi.finishSession(session.value.id);
      const data = res.data?.data as FinishSessionResponse;
      if (!data) return null;

      applySessionState(data.session);
      lastRevealedSessionId.value = data.session.id;
      localStorage.setItem(LAST_REVEALED_KEY, data.session.id);
      if (data.markers != null) {
        const puzzleStore = useMapPuzzleStore();
        puzzleStore.markers = data.markers;
      }
      cleanupAfterReveal();
      redWalkPuzzles.value = [];
      currentRedWalkIndex.value = 0;
      hasWrongRounds.value = false;
      wrongCount.value = 0;

      loadDailyCards();
      loadTodayCard();

      return data;
    } catch {
      return null;
    }
  }

  function requestShowWalkMarkers() {
    showWalkMarkers.value = true;
  }

  async function loadDailyCards() {
    try {
      const res = await gambits.walkApi.getWalkHistory();
      const history = res.data?.data;
      dailyCards.value = history?.days ?? [];
      monthCards.value = history?.months ?? [];
    } catch {
      dailyCards.value = [];
      monthCards.value = [];
    }
  }

  async function loadStreakRecords() {
    try {
      const res = await gambits.walkApi.getStreakRecords();
      streakRecords.value = res.data?.data ?? [];
    } catch {
      streakRecords.value = [];
    }
  }

  async function loadFieldStats() {
    try {
      const res = await gambits.walkApi.getFieldStats();
      fieldStats.value = res.data?.data ?? [];
    } catch {
      fieldStats.value = [];
    }
  }

  async function loadTraceTotals() {
    try {
      const res = await gambits.walkApi.getTraceTotals();
      traceTotals.value = res.data?.data ?? { traceMine: 0, traceOthers: 0 };
    } catch {
      traceTotals.value = { traceMine: 0, traceOthers: 0 };
    }
  }

  async function loadTodayCard() {
    try {
      const res = await gambits.walkApi.getDailyCards(1);
      const cards = res.data?.data ?? [];
      const today = new Date().toLocaleDateString('en-CA');
      todayCard.value = cards.find((c) => c.date === today) ?? null;
    } catch {
      todayCard.value = null;
    }
  }

  async function loadSummary(sessionId: string) {
    try {
      const res = await gambits.walkApi.getSummary(sessionId);
      const data = res.data?.data;
      if (data) lastSummary.value = data;
      return data ?? null;
    } catch {
      return null;
    }
  }

  async function loadHistorySummary(sessionId: string) {
    try {
      const res = await gambits.walkApi.getSummary(sessionId);
      historySummary.value = res.data?.data ?? null;
      return historySummary.value;
    } catch {
      historySummary.value = null;
      return null;
    }
  }

  async function deleteSession(sessionId: string) {
    try {
      await gambits.walkApi.deleteSession(sessionId);
      historySummary.value = null;
      lastRevealedSessionId.value = null;
      localStorage.removeItem(LAST_REVEALED_KEY);
      const walkStore = useMapPuzzleStore();
      walkStore.markers = [];
      await Promise.all([loadDailyCards(), loadStreakRecords(), loadFieldStats(), loadTraceTotals()]);
      return true;
    } catch {
      return false;
    }
  }

  function reset() {
    session.value = null;
    currentPuzzle.value = null;
    preloadedPuzzles.value = [];
    sessionEndState.value = null;
    boardEndState.value = null;
    lastReveal.value = null;
    lastSummary.value = null;
    localTimeMs.value = 0;
    breakTimeMs.value = 0;
    currentSpeedMps.value = 0;
    warmupReady.value = false;
    warmupConsecutiveTicks = 0;
    warmupLastFixTimestamp = 0;
    warmupTicks.value = 0;
    resetBreakWarmup();
    startSpot.value = null;
    revealSpot.value = null;
    historySummary.value = null;
    roundLog.value = [];
    syncQueue.value = [];
    syncInProgress = false;
    pendingSubmitRound.value = null;
    showWalkMarkers.value = false;
    clearPersistedSpots();
    clearPersistedSession();
    clearWarmupTimeout();
    stopTimerLoop();
    stopSpeedTracking();
  }

  // --- Global event listeners ---

  function onOnline() {
    flushSyncQueue();
  }

  function onVisibilityChange() {
    if (
      document.visibilityState === 'visible' &&
      (session.value?.status === 'active' || session.value?.status === 'walking_reds')
    ) {
      recalcTimerFromSession();
      if (timerInterval == null && (isThinking.value || isBreak.value)) {
        startTimerLoop();
      }
      flushSyncQueue();
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function dispose() {
    clearWarmupTimeout();
    stopTimerLoop();
    stopSpeedTracking();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  }

  return {
    isLoading,
    dailyCards,
    monthCards,
    todayCard,
    streakRecords,
    fieldStats,
    fieldBrilliantMap,
    traceTotals,
    settings,
    customPresets,
    allPresets,
    updateSettings,
    addCustomPreset,
    removeCustomPreset,
    session,
    currentPuzzle,
    sessionEndState,
    boardEndState,
    lastReveal,
    lastSummary,
    historySummary,
    showRevealOnReturn,
    localTimeMs,
    breakTimeMs,
    currentSpeedMps,
    hasGpsSignal,
    startSpot,
    revealSpot,
    sessionStartSpot,
    sessionRevealSpot,
    setSpot,
    saveSessionSpot,
    hasActiveSession,
    isThinking,
    isBreak,
    isWarmup,
    warmupProgress,
    warmupReady,
    breakNeedsWarmup,
    breakWarmupReady,
    requestStartFromBreak,
    isIdle,
    isRevealed,
    lastRevealedSessionId,
    isWalkingReds,
    redWalkPuzzles,
    currentRedWalkIndex,
    hasWrongRounds,
    wrongCount,
    redWalkProgress,
    createSession,
    loadActiveSession,
    startThink,
    submitMove,
    reveal,
    walkReds,
    finishSession,
    requestShowWalkMarkers,
    showWalkMarkers,
    loadSummary,
    loadDailyCards,
    loadStreakRecords,
    loadFieldStats,
    loadTraceTotals,
    loadTodayCard,
    loadHistorySummary,
    deleteSession,
    reset,
    dispose,
    startSpeedTracking,
    stopSpeedTracking,
    startTimerLoop,
    recalcTimerFromSession,
    flushSyncQueue,
    syncQueue,
    roundLog,
    getProximityLocation,
  };
});
